import React from 'react';
import PropTypes from 'prop-types';

import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import { ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING } from 'common/utility/constants';

import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFlex from 'client/components/SrFlex';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import { SR_BUTTON_THEME_ADOBE_GREEN } from 'client/components/SrButton/SrButton';

import useAddressVerificationController from './useAddressVerificationController';
import UPAddressVerificationForm from './UPAddressVerificationForm';

import './UPAddressVerificationDetailsModal.less';

const UPAddressVerificationDetailsModal = ({ item, onClose, reloadItems }) => {
  const dispatch = useDispatch();

  const {
    state: { isSubmitting, errorMessage, successModalType, listing, address, isListingLoading },
    handlers: { handleListingAddressUpdate, handleCloseSuccessModal },
  } = useAddressVerificationController({ item, reloadItems });

  if (!item) {
    return null;
  }

  const displayStatus =
    ADDRESS_RESOLUTION_STATUS_TO_LABEL_MAPPING[item.status] ?? camelCaseToTitleCase(item.status);
  const displaySearchTerm = item.searchTerm || listing?.addressStreetDisplay || '—';
  const addressFields = address
    ? {
        streetNumber: address.streetNumber,
        streetName: address.streetName,
        streetPrefix: address.streetPrefix,
        streetSuffix: address.streetSuffix,
        unitNumber: address.unitNumber,
        city: address.city,
        stateCode: address.stateCode,
        county: address.county,
        countryCode: address.countryCode,
        zip: address.zip,
        latitude: address.latitude,
        longitude: address.longitude,
      }
    : null;

  return (
    <>
      <SrDarkModal onClose={onClose}>
        <SrModalContent width={550} innerStyle={{ padding: 0 }}>
          <SrBox styleName="UPAddressVerificationDetailsModal">
            <SrBox styleName="modalHeader">
              <SrText
                font="greycliff"
                weight="800"
                lineHeight="25px"
                fontSize="25px"
                styleName="modalTitle"
              >
                Update Listing Address
              </SrText>
            </SrBox>
            <SrBox padding="25px 40px 0px 40px">
              <SrBox flex="1">
                <SrFlex direction="row" gap="8px" justify="between" marginBottom="20px">
                  <SrBox display="flex" flexDirection="column" gap="4px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="14px"
                      color="grey300"
                      styleName="fieldLabel"
                      display="block"
                    >
                      Partner
                    </SrText>
                    <SrText font="greycliff" weight="600" fontSize="16px" display="block">
                      {item.partnerName}
                    </SrText>
                  </SrBox>
                  <SrBox display="flex" flexDirection="column" gap="4px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="14px"
                      color="grey300"
                      styleName="fieldLabel"
                      display="block"
                    >
                      Status
                    </SrText>
                    <SrText font="greycliff" weight="600" fontSize="16px" display="block">
                      {displayStatus}
                    </SrText>
                  </SrBox>
                  <SrBox display="flex" flexDirection="column" gap="4px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="14px"
                      color="grey300"
                      styleName="fieldLabel"
                      display="block"
                    >
                      Source
                    </SrText>
                    <SrText font="greycliff" weight="600" fontSize="16px" display="block">
                      {camelCaseToTitleCase(item.source)}
                    </SrText>
                  </SrBox>
                </SrFlex>
                <SrBox marginBottom="24px">
                  <SrText
                    font="greycliff"
                    weight="700"
                    fontSize="14px"
                    color="grey300"
                    styleName="fieldLabel fieldLabelSpaced"
                  >
                    Listing Address
                  </SrText>
                  <SrFlex align="center" gap="8px">
                    <SrText font="greycliff" weight="600" fontSize="16px">
                      {displaySearchTerm}
                    </SrText>
                  </SrFlex>
                </SrBox>
              </SrBox>
            </SrBox>

            {errorMessage && (
              <SrBox padding="0 40px" marginBottom="16px">
                <SrErrorMessage>{errorMessage}</SrErrorMessage>
              </SrBox>
            )}

            <SrBox padding="0 40px 40px">
              <SrBox styleName="tabContent">
                <UPAddressVerificationForm
                  dispatch={dispatch}
                  disabled={isSubmitting || isListingLoading}
                  onRegister={handleListingAddressUpdate}
                  initialAddressFields={addressFields}
                  forceManual
                  submitLabel="Update Address"
                  submittingLabel="Updating..."
                />
              </SrBox>
            </SrBox>
          </SrBox>
        </SrModalContent>
      </SrDarkModal>

      {successModalType && (
        <SrSimpleMessageModal
          iconUrl="/images/checkmark-circle-large-green.svg"
          headline="Success!"
          underline
          message={
            <SrBox maxWidth="280px" margin="0 auto">
              <SrText font="greycliff" weight="700" fontSize="16px" display="block">
                {displaySearchTerm}
              </SrText>
              <SrText fontSize="16px" display="block">
                Address has been updated.
              </SrText>
            </SrBox>
          }
          srMultiButtons={[
            {
              buttonText: (
                <SrFlex align="center" justify="center">
                  Continue
                </SrFlex>
              ),
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
              onButtonClick: () => {
                handleCloseSuccessModal();
                onClose();
              },
            },
          ]}
          maxWidth={360}
          maxIconHeight="100"
          maxIconWidth="100"
        />
      )}
    </>
  );
};

UPAddressVerificationDetailsModal.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  reloadItems: PropTypes.func,
};

UPAddressVerificationDetailsModal.displayName = 'UPAddressVerificationDetailsModal';

export default UPAddressVerificationDetailsModal;
